import { ReadMoreText } from '@/components/ui/read-more-text';
import { getInvitationMessageDetails } from '@/services/invitation-message-service';
import { Box, Button, SimpleGrid, Skeleton } from '@chakra-ui/react';
import type { Invitation, MessageData } from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { InvitationMessageDetails } from './invitation';
import MessageContainer from './MessageContainer';

interface InvitationMessageProps {
  message: MessageData;
  time: string;
}

/**
 * Component to render an invitation message with program details and offer information
 */
const InvitationMessage: React.FC<InvitationMessageProps> = ({ message, time }) => {
  const { data: details, isLoading } = useQuery<Invitation, Error>({
    queryKey: ['invitationMessageDetails', message.id],
    queryFn: () => getInvitationMessageDetails(message.id),
    enabled: !!message.id,
  });

  const messageContent = message.content || '';

  return (
    <MessageContainer
      time={time}
      bg="gray.100"
      px={3}
      py={3}
      maxW="490px"
      rounded="lg"
      isSelf={false}
    >
      <ReadMoreText
        content={messageContent}
        fontSize="15px"
        lineHeight="tall"
        maxLines={2}
        expandText="See more"
      />

      {/* Message content with read more functionality */}
      <Box rounded="xl" bg="white" p={5} mt={4}>
        {/* Invitation details */}
        {isLoading ? (
          <Box mt={4}>
            <SimpleGrid columns={2} gap={4}>
              <Box>
                <Skeleton height="20px" width="60px" mb={1} />
                <Skeleton height="24px" width="120px" />
              </Box>
              <Box>
                <Skeleton height="20px" width="60px" mb={1} />
                <Skeleton height="24px" width="120px" />
              </Box>
            </SimpleGrid>
            <SimpleGrid columns={2} gap={4} mt={4}>
              <Box>
                <Skeleton height="20px" width="60px" mb={1} />
                <Skeleton height="24px" width="120px" />
              </Box>
              <Box>
                <Skeleton height="20px" width="60px" mb={1} />
                <Skeleton height="24px" width="120px" />
              </Box>
            </SimpleGrid>
          </Box>
        ) : (
          <InvitationMessageDetails
            program={details?.program || {}}
            sentDate={details?.createdAt ? new Date(details.createdAt) : null}
            expireDate={details?.expiresAt ? new Date(details.expiresAt) : null}
            status={details?.status}
          />
        )}
        <Button w="full" mt={2} variant="solid" colorPalette="gray" size="sm" asChild>
          <Link to={`/programs/${details?.program?.id}`}>View Program Details</Link>
        </Button>
      </Box>
    </MessageContainer>
  );
};

export default InvitationMessage;
